import { google, outlook, office365, ics, yahoo } from 'calendar-link';
import { eckoDispatch } from '../utils/analytics';
import { closeSheet, openSheet } from './ecko-sheets';
import { showToast } from './toast';

const EVENT_PROVIDER_TOAST_MESSAGES = {
	google: 'Opening Google Calendar...',
	outlook: 'Opening Outlook...',
	office: 'Opening Outlook...',
	m365: 'Opening Outlook...',
	yahoo: 'Opening Yahoo Calendar...',
	apple: 'Opening Apple Calendar...',
	ical: '',
	appleDownload: 'Downloading .ics event file...',
	icsDownload: 'Downloading .ics event file...',
	ics: 'Downloading .ics event file...'
};

function closeContainingDialog(element) {
	const dialog = element.closest('dialog');
	if (dialog?.open) {
		closeSheet(dialog);
	}
}

function showEventProviderToast(provider, options = {}) {
	const key = options.download ? `${provider}Download` : provider;
	const message = EVENT_PROVIDER_TOAST_MESSAGES[key];
	if (!message) return;

	showToast({
		message,
		mode: 'success',
		icon: options.download || provider === 'ics' ? 'download' : 'external'
	});
}

export function initCalendarModals() {
	document.addEventListener('DOMContentLoaded', () => {
		const subscribeModal = document.getElementById('subscribe-modal');
		const eventModal = document.getElementById('event-modal');

		if (!subscribeModal && !eventModal) {
			return;
		}

		const eventTitleEl = document.getElementById('event-modal-title');
		const eventDateEl = document.getElementById('event-modal-date');
		const eventButtonsEl = document.getElementById('event-modal-buttons');

		document.addEventListener('click', e => {
			const providerBtn = e.target.closest('#subscribe-modal [data-provider]');
			if (!providerBtn) return;

			const provider = providerBtn.getAttribute('data-provider');

			eckoDispatch('ecko:subscribeCalendarProvider', {
				provider
			});
			showEventProviderToast(provider);
			closeContainingDialog(providerBtn);
		});

		document.addEventListener('click', e => {
			const btn = e.target.closest('[data-action="subscribe-to-calendar"]');
			if (!btn || !subscribeModal) return;

			e.preventDefault();
			eckoDispatch('ecko:subscribeCalendarModalOpen');
			openSheet(subscribeModal);
		});

		document.addEventListener('click', e => {
			const btn = e.target.closest('[data-action="add-to-calendar"]');
			if (!btn || !eventModal || !eventTitleEl || !eventDateEl || !eventButtonsEl) return;

			e.preventDefault();

			const anchorId = getArticleAnchor(btn);
			const eventUrl = anchorId ? buildEventUrl(anchorId) : window.location.href;

			eventTitleEl.innerHTML = btn.dataset.eventTitle;
			eventDateEl.innerHTML = btn.dataset.eventDateDisplay;

			const event = normalizeCalendarEvent({
				title: btn.dataset.eventTitle,
				description: btn.dataset.eventDescription,
				location: btn.dataset.eventLocation,
				url: eventUrl,
				start: btn.dataset.startDatetime,
				end: btn.dataset.endDatetime,
				allDay: btn.dataset.allDay === 'true'
			});

			if (!event) {
				eventButtonsEl.innerHTML = '';
				return;
			}

			const googleUrl = event.allDay
				? buildGoogleAllDayEventUrl(event)
				: google(event);
			const yahooUrl = event.allDay
				? buildYahooAllDayEventUrl(event)
				: yahoo(event);
			const outlookUrl = event.allDay
				? buildOutlookAllDayEventUrl(event, 'outlook')
				: outlook(event);
			const officeUrl = event.allDay
				? buildOutlookAllDayEventUrl(event, 'office')
				: office365(event);
			const icalObject = event.allDay
				? buildAllDayIcs(event)
				: ics(event);

			eventButtonsEl.innerHTML = `
				<a href="${googleUrl}" target="_blank" class="button button-plain has-icon-left align-left" data-provider="google"><svg width="640" height="640" class="fa" viewBox="0 0 640 640" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M635 327.484C635 510.064 509.904 640 325.164 640C148.041 640 5 497.032 5 320C5 142.968 148.041 0 325.164 0C411.402 0 483.955 31.6129 539.854 83.7419L452.713 167.484C338.719 57.5484 126.74 140.129 126.74 320C126.74 431.613 215.947 522.065 325.164 522.065C451.939 522.065 499.447 431.226 506.934 384.129H325.164V274.065H629.965C632.934 290.452 635 306.194 635 327.484Z" /></svg> Google Calendar</a>
				<button class="button button-plain has-icon-left align-left" data-provider="apple"><svg width="640" height="640" class="fa" viewBox="0 0 640 640" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M501.81 338.204C501.524 285.766 525.251 246.188 573.277 217.039C546.405 178.604 505.812 157.457 452.213 153.314C401.471 149.313 346.013 182.89 325.717 182.89C304.277 182.89 255.108 154.742 216.516 154.742C136.759 156.028 52 218.325 52 345.063C52 382.498 58.8608 421.219 72.5824 461.084C90.8778 513.522 156.913 642.117 225.807 639.974C261.826 639.116 287.268 614.397 334.15 614.397C379.603 614.397 403.187 639.974 443.351 639.974C512.816 638.973 572.562 522.095 590 469.514C496.808 425.649 501.81 340.919 501.81 338.204ZM420.91 103.59C459.931 57.2961 456.358 15.1456 455.214 0C420.767 2.00036 380.889 23.4328 358.163 49.8662C333.149 78.1571 318.427 113.163 321.572 152.599C358.877 155.457 392.895 136.31 420.91 103.59Z" /></svg> Apple Calendar</button>
				<a href="${officeUrl}" target="_blank" class="button button-plain has-icon-left align-left" data-provider="office"><svg width="640" height="640" class="fa" viewBox="0 0 640 640" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M382.087 624.06C389.847 626.647 398.902 626.647 406.662 624.06L560.586 573.614C570.933 569.734 578.694 560.68 578.694 549.039V83.3888C578.694 75.628 573.52 67.8672 565.759 65.2802L409.249 12.2479C400.195 9.66094 391.141 9.66094 383.38 12.2479L74.2404 131.247C66.4795 133.834 61.3057 141.595 61.3057 149.356V499.887C61.3057 507.647 66.4795 515.408 74.2404 517.995L382.087 624.06ZM397.608 537.397C397.608 545.158 391.141 551.625 384.673 550.332L125.979 515.408C119.512 514.115 114.338 508.941 114.338 502.474V497.3C114.338 492.126 116.925 488.245 123.392 485.658L172.544 462.376C177.718 459.789 180.305 455.909 180.305 450.735V175.225C180.305 168.758 184.185 163.584 190.653 162.291L384.673 118.313C392.434 117.019 400.195 122.193 400.195 131.247V537.397H397.608Z" /></svg> Outlook Work or School (M365)</a>
				<a href="${outlookUrl}" target="_blank" class="button button-plain has-icon-left align-left" data-provider="outlook"><svg width="640" height="640" class="fa" viewBox="0 0 640 640" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M640 196.978V476.441C640 482.576 637.867 487.75 633.653 491.804C629.44 495.912 624.267 497.939 618.187 497.939H390.267V312.332L432.933 345.111C435.653 347.378 439.04 348.472 443.04 348.472C446.987 348.472 450.427 347.378 453.413 345.085L640 196.978ZM390.267 143.075H618.187C623.813 143.075 628.667 144.755 632.667 148.196C636.667 151.61 638.907 156.197 639.28 161.798L442.773 318.52L390.267 277.206V143.075ZM357.44 23V617L0 555.229V86.4781L357.44 23ZM249.467 321.187C248.933 290.968 241.12 265.924 226.027 246.133C211.227 226.397 192.027 215.968 169.2 215.088C147.227 215.968 128.427 226.37 113.2 246.133C98 265.924 90.4 290.995 90 321.187C90.4 350.979 98.4 375.783 113.6 395.627C128.827 415.364 147.573 425.846 169.627 426.993C192.427 426.059 211.6 415.684 226.827 395.787C242.027 375.837 250.027 350.979 250.427 321.187H249.467ZM166.267 254.988C177.76 255.522 187.44 261.816 195.147 274.112C202.747 286.407 206.747 301.983 206.747 320.84C206.747 340.07 202.773 355.7 195.147 368.075C187.12 380.451 177.547 386.852 165.92 386.852C154.293 386.852 144.72 380.824 136.72 368.449C128.72 356.073 125.12 340.444 125.12 321.667C125.12 302.864 128.72 287.234 136.72 275.259C144.293 263.256 153.947 256.855 165.547 256.028L166.267 254.988Z"/></svg> Personal Outlook.com</a>
				<a href="${yahooUrl}" target="_blank" class="button button-plain has-icon-left align-left" data-provider="yahoo"><svg width="640" height="640" class="fa" viewBox="0 0 640 640" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 173.518H122.06L193.135 355.361L265.135 173.518H383.968L205.027 604H85.4225L134.408 489.93L0.00375 173.518H0ZM522.35 318.816H389.25L507.384 35L640 35.0063L522.35 318.816ZM424.115 345.98C464.955 345.98 498.059 379.088 498.059 419.922C498.059 460.759 464.955 493.867 424.115 493.867C383.281 493.867 350.182 460.759 350.182 419.922C350.182 379.088 383.286 345.98 424.115 345.98Z" fill="currentColor"/></svg> Yahoo Calendar</a>
				<button class="button button-plain has-icon-left align-left" data-provider="ics"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-download"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" /><path d="M7 11l5 5l5 -5" /><path d="M12 4l0 12" /></svg> Download .ics</button>
			`;

			eventButtonsEl.querySelectorAll('[data-provider]').forEach(providerButton => {
				providerButton.addEventListener('click', () => {
					const provider = providerButton.getAttribute('data-provider');
					const isDownloadProvider = provider === 'apple' || provider === 'ics';

					eckoDispatch('ecko:eventAddToCalendarProvider', {
						provider,
						title: event.title
					});
					showEventProviderToast(provider, { download: isDownloadProvider });
					closeContainingDialog(providerButton);
				});
			});

			const icsButtons = eventButtonsEl.querySelectorAll('[data-provider="ics"]');
			icsButtons.forEach(providerButton => {
				providerButton.addEventListener('click', () => downloadIcs(icalObject, event.title));
			});

			const appleButtons = eventButtonsEl.querySelectorAll('[data-provider="apple"]');
			appleButtons.forEach(providerButton => {
				providerButton.addEventListener('click', () => downloadIcs(icalObject, event.title));
			});

			eckoDispatch('ecko:eventAddToCalendarOpen', {
				title: event.title
			});
			openSheet(eventModal);
		});


		function slugify(str) {
			return str
				.toLowerCase()
				.trim()
				.replace(/[^a-z0-9\s]/g, '')
				.replace(/\s+/g, '-');
		}

		function normalizeCalendarEvent(event) {
			if (!event.allDay) {
				return event;
			}

			const start = normalizeAllDayDate(event.start);
			const inclusiveEnd = normalizeAllDayDate(event.end) || start;

			if (!start || !inclusiveEnd) {
				return null;
			}

			return {
				...event,
				start,
				end: addOneDay(inclusiveEnd),
				allDay: true
			};
		}

		function normalizeAllDayDate(value) {
			if (!value) {
				return '';
			}

			const raw = String(value).trim();

			if (/^\d{8}$/.test(raw)) {
				return raw;
			}

			const dateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
			if (dateMatch) {
				return `${dateMatch[1]}${dateMatch[2]}${dateMatch[3]}`;
			}

			const parsed = new Date(raw);
			if (Number.isNaN(parsed.getTime())) {
				return '';
			}

			return [
				parsed.getFullYear(),
				String(parsed.getMonth() + 1).padStart(2, '0'),
				String(parsed.getDate()).padStart(2, '0')
			].join('');
		}

		function formatAllDayDateTime(yyyymmdd) {
			return `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}T00:00:00`;
		}

		function buildGoogleAllDayEventUrl(event) {
			const start = event.start;
			const endExclusive = event.end;

			const params = new URLSearchParams({
				action: 'TEMPLATE',
				text: event.title || '',
				details: event.description || '',
				location: event.location || '',
				dates: `${start}/${endExclusive}`
			});

			return `https://calendar.google.com/calendar/render?${params.toString()}`;
		}

		function buildYahooAllDayEventUrl(event) {
			const params = new URLSearchParams({
				v: '60',
				view: 'd',
				type: '20',
				title: event.title || '',
				st: event.start,
				desc: event.description || '',
				in_loc: event.location || ''
			});

			return `https://calendar.yahoo.com/?${params.toString()}`;
		}

		function buildOutlookAllDayEventUrl(event, service) {
			const baseUrl = service === 'office'
				? 'https://outlook.office.com/calendar/0/action/compose'
				: 'https://outlook.live.com/calendar/0/action/compose';

			const params = new URLSearchParams({
				path: '/calendar/action/compose',
				rru: 'addevent',
				startdt: formatAllDayDateTime(event.start),
				enddt: formatAllDayDateTime(event.end),
				subject: event.title || '',
				body: event.description || '',
				location: event.location || '',
				allday: 'true'
			});

			return `${baseUrl}?${params.toString().replace(/\+/g, '%20')}`;
		}

		function buildAllDayIcs(event) {
			const timestamp = formatUtcIcsDateTime(new Date());
			const uidSeed = `${event.title || 'event'}-${event.start}-${event.end}-${event.url || window.location.href}`;
			const uid = `${slugify(uidSeed) || 'event'}@ecko`;
			const lines = [
				'BEGIN:VCALENDAR',
				'VERSION:2.0',
				'PRODID:-//Ecko//AddToCalendar//EN',
				'CALSCALE:GREGORIAN',
				'METHOD:PUBLISH',
				'BEGIN:VEVENT',
				`UID:${escapeIcsText(uid)}`,
				`DTSTAMP:${timestamp}`,
				`DTSTART;VALUE=DATE:${event.start}`,
				`DTEND;VALUE=DATE:${event.end}`,
				`SUMMARY:${escapeIcsText(event.title || '')}`,
				event.description ? `DESCRIPTION:${escapeIcsText(event.description)}` : '',
				event.location ? `LOCATION:${escapeIcsText(event.location)}` : '',
				event.url ? `URL:${escapeIcsText(event.url)}` : '',
				'END:VEVENT',
				'END:VCALENDAR'
			].filter(Boolean);

			return `data:text/calendar;charset=utf8,${encodeURIComponent(lines.join('\r\n') + '\r\n')}`;
		}

		function formatUtcIcsDateTime(date) {
			return [
				date.getUTCFullYear(),
				String(date.getUTCMonth() + 1).padStart(2, '0'),
				String(date.getUTCDate()).padStart(2, '0'),
				'T',
				String(date.getUTCHours()).padStart(2, '0'),
				String(date.getUTCMinutes()).padStart(2, '0'),
				String(date.getUTCSeconds()).padStart(2, '0'),
				'Z'
			].join('');
		}

		function escapeIcsText(value) {
			return String(value)
				.replace(/\\/g, '\\\\')
				.replace(/\r\n|\r|\n/g, '\\n')
				.replace(/,/g, '\\,')
				.replace(/;/g, '\\;');
		}

		function addOneDay(yyyymmdd) {
			const y = parseInt(yyyymmdd.slice(0, 4));
			const m = parseInt(yyyymmdd.slice(4, 6));
			const d = parseInt(yyyymmdd.slice(6, 8));

			const date = new Date(Date.UTC(y, m - 1, d));
			date.setUTCDate(date.getUTCDate() + 1);

			const y2 = date.getUTCFullYear();
			const m2 = String(date.getUTCMonth() + 1).padStart(2, '0');
			const d2 = String(date.getUTCDate()).padStart(2, '0');

			return `${y2}${m2}${d2}`;
		}

		function getArticleAnchor(el) {
			const article = el.closest('article');
			if (!article || !article.id) return null;

			return article.id;
		}

		function buildEventUrl(anchorId) {
			const url = new URL(window.location.href);
			url.hash = '';
			url.hash = anchorId;

			return url.toString();
		}

		function downloadIcs(content, eventTitle) {
			const raw = content.replace(/^data:text\/calendar[^,]*,/i, '');
			const decoded = decodeURIComponent(raw);
			const slug = slugify(eventTitle);
			const filename = `${slug}.ics`;

			const blob = new Blob([decoded], { type: 'text/calendar' });
			const url = URL.createObjectURL(blob);

			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			a.click();

			URL.revokeObjectURL(url);
		}
	});
}
